import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Alert,
  FormControlLabel,
  Switch,
  Chip,
  OutlinedInput,
} from '@mui/material';
import { User, CreateUserData, UpdateUserData, UserRole } from '../../types/user';
import { usersAPI } from '../../services/users-api';

interface UserFormProps {
  open: boolean;
  user: User | null;
  isCreating: boolean;
  onClose: () => void;
  onSave: () => void;
}

const UserForm: React.FC<UserFormProps> = ({
  open,
  user,
  isCreating,
  onClose,
  onSave,
}) => {
  const [formData, setFormData] = useState({
    email: '',
    displayName: '',
    role: 'viewer' as UserRole,
    phoneNumber: '',
    department: '',
    notes: '',
    isActive: true,
    companyIds: [] as string[],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (user) {
      setFormData({
        email: user.email,
        displayName: user.displayName || '',
        role: user.role,
        phoneNumber: user.phoneNumber || '',
        department: user.department || '',
        notes: user.notes || '',
        isActive: user.isActive,
        companyIds: user.companyIds || [],
      });
    } else {
      setFormData({
        email: '',
        displayName: '',
        role: 'viewer',
        phoneNumber: '',
        department: '',
        notes: '',
        isActive: true,
        companyIds: [],
      });
    }
  }, [user]);

  const handleChange = (field: string) => (event: any) => {
    setFormData({
      ...formData,
      [field]: field === 'isActive' ? event.target.checked : event.target.value,
    });
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');

      if (isCreating) {
        const createData: CreateUserData = {
          email: formData.email,
          displayName: formData.displayName || undefined,
          role: formData.role,
          phoneNumber: formData.phoneNumber || undefined,
          department: formData.department || undefined,
          notes: formData.notes || undefined,
          companyIds: formData.companyIds.length > 0 ? formData.companyIds : undefined,
        };
        await usersAPI.createUser(createData);
      } else if (user) {
        const updateData: UpdateUserData = {
          displayName: formData.displayName || undefined,
          role: formData.role,
          phoneNumber: formData.phoneNumber || undefined,
          department: formData.department || undefined,
          notes: formData.notes || undefined,
          isActive: formData.isActive,
          companyIds: formData.companyIds.length > 0 ? formData.companyIds : undefined,
        };
        await usersAPI.updateUser(user.id, updateData);
      }

      onSave();
    } catch (err: any) {
      setError(err.message || 'Failed to save user');
    } finally {
      setLoading(false);
    }
  };

  const isEmailValid = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(formData.email);
  };

  const canSubmit = () => {
    if (isCreating) {
      return formData.email && isEmailValid();
    }
    return true;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isCreating ? 'Create New User' : 'Edit User'}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Email"
            value={formData.email}
            onChange={handleChange('email')}
            disabled={!isCreating}
            required={isCreating}
            error={isCreating && !!formData.email && !isEmailValid()}
            helperText={
              isCreating && formData.email && !isEmailValid()
                ? 'Please enter a valid email'
                : ''
            }
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Display Name"
            value={formData.displayName}
            onChange={handleChange('displayName')}
            sx={{ mb: 2 }}
          />

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Role</InputLabel>
            <Select
              value={formData.role}
              onChange={handleChange('role')}
              label="Role"
            >
              <MenuItem value="viewer">Viewer</MenuItem>
              <MenuItem value="analyst">Analyst</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Department"
            value={formData.department}
            onChange={handleChange('department')}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Phone Number"
            value={formData.phoneNumber}
            onChange={handleChange('phoneNumber')}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Notes"
            value={formData.notes}
            onChange={handleChange('notes')}
            multiline
            rows={3}
            sx={{ mb: 2 }}
          />

          {!isCreating && (
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={handleChange('isActive')}
                />
              }
              label="Active"
              sx={{ mb: 2 }}
            />
          )}

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Company Access</InputLabel>
            <Select
              multiple
              value={formData.companyIds}
              onChange={handleChange('companyIds')}
              input={<OutlinedInput label="Company Access" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
            >
              <MenuItem value="demo-company">Demo Company</MenuItem>
              <MenuItem value="company-1">Company 1</MenuItem>
              <MenuItem value="company-2">Company 2</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !canSubmit()}
        >
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserForm;